import React, { useEffect } from 'react';
import AudioPlayer from './AudioPlayer';
import { PLACEHOLDER_AUDIO } from '../utils/placeholders';

const AudioModal = ({ 
  isOpen, 
  onClose, 
  article,
  sectionTitle = ""
}) => {
  // Cerrar modal con tecla Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevenir scroll del body cuando el modal está abierto
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !article) return null;

  // Función para obtener imagen con fallbacks
  const getImageUrl = (article) => {
    return article.imageUrl || 
           article.image || 
           article.thumbnail || 
           PLACEHOLDER_AUDIO;
  };

  // Función para obtener el icono según la sección
  const getSectionIcon = (sectionTitle) => {
    const icons = {
      'Evangelio': '📖',
      'Buenos días': '🌅', 
      'Clave agustiniana': '🔑',
      'Santos Agustinos': '👼',
      'Vocaciones': '✨',
      'Vamos al cole': '🎒',
      'Te interesa': '🌍',
      'Está pasando': '📰',
      'Píldoras de San Agustín': '💊',
      'Habla San Agustín': '🗣️',
      'Orando con San Agustín': '🙏',
      'Historia y personajes': '📚',
      'Sabías que…': '📚',
      'Reflexión agustiniana': '🤔',
      'Domingo con San Agustín': '☀️'
    };
    return icons[sectionTitle] || '🎵';
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      {/* Contenedor del modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        style={{
          maxHeight: '90vh',
          animation: 'modalSlideIn 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón de cerrar */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black bg-opacity-30 backdrop-blur-sm text-white hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center"
          style={{
            fontSize: '18px',
            lineHeight: '1'
          }}
        >
          ×
        </button>

        {/* Header con imagen/icono */}
        <div className="relative">
          {article.imageUrl ? (
            <div className="w-full h-48 overflow-hidden bg-gradient-to-br from-red-50 to-red-100">
              <img 
                src={getImageUrl(article)}
                alt={article.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Si falla la imagen, mostrar un fondo con icono
                  e.target.parentNode.innerHTML = `
                    <div class="w-full h-48 flex items-center justify-center text-6xl" 
                         style="background: linear-gradient(135deg, #e62c19 0%, #a01916 100%)">
                      ${getSectionIcon(sectionTitle)}
                    </div>
                  `;
                }}
              />
            </div>
          ) : (
            <div 
              className="w-full h-48 flex items-center justify-center text-6xl"
              style={{
                background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)'
              }}
            >
              {getSectionIcon(sectionTitle)}
            </div>
          )}

          {/* Overlay con gradiente para mejor legibilidad */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)'
            }}
          />
        </div>

        {/* Contenido del modal */}
        <div className="p-6">
          {/* Información del artículo */}
          <div className="mb-6">
            {/* Sección */}
            {sectionTitle && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-red-600 font-semibold bg-red-50 px-3 py-1 rounded-full">
                  {getSectionIcon(sectionTitle)} {sectionTitle}
                </span>
              </div>
            )}

            {/* Título */}
            <h2 className="text-lg font-bold text-gray-800 mb-2 leading-tight">
              {article.title}
            </h2>

            {/* Descripción si existe */}
            {article.description && (
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                {article.description}
              </p>
            )}

            {/* Metadatos */}
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {article.date && (
                <span className="flex items-center gap-1">
                  📅 {new Date(article.date).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
              )}

              {article.author && article.author !== 'Agustinos' && (
                <span className="flex items-center gap-1">
                  ✏️ {article.author}
                </span>
              )}
            </div>
          </div>

          {/* Reproductor de audio */}
          <AudioPlayer
            audioUrl={article.audioUrl}
            title={`Audio: ${article.title}`}
            showTitle={false}
            size="normal"
            theme="agustinos"
            autoPlay={true}
          />

          {/* Botón para ver artículo completo */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                onClose();
                // Aquí podrías disparar un evento para navegar al artículo completo
                window.dispatchEvent(new CustomEvent('openArticleFromAudio', {
                  detail: { article, sectionTitle }
                }));
              }}
              className="w-full py-3 px-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center gap-2"
            >
              📖 Ver artículo completo
            </button>
          </div>
        </div>
      </div>

      {/* CSS para animaciones */}
      <style jsx>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AudioModal;
